// Раздел Актов: реестр КС-2 и КС-3, создание, PDF, подпись, выгрузка 1С.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileCheck, Plus, Download, PenLine, Banknote, FileSpreadsheet, Check, FileDown,
} from 'lucide-react';
import { ks2Api, ks3Api, contractsApi, exportApi, getErrorMessage } from '@/api';
import { AppLayout, PageHeader } from '@/components/layout/AppLayout';
import { Spinner } from '@/components/ui/Spinner';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/hooks/useAuth';
import { formatMoney, formatDate } from '@/lib/format';
import type { KS2Act, KS3Certificate } from '@/types';

const KS2_COLOR: Record<string, string> = {
  draft: 'var(--text-muted)', review: 'var(--yellow)', signing: 'var(--blue)',
  signed: 'var(--green)', rejected: 'var(--red)',
};
const KS3_COLOR: Record<string, string> = {
  draft: 'var(--text-muted)', signing: 'var(--blue)', signed: 'var(--green)', paid: 'var(--accent)',
};

function openBlob(blob: Blob) {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function ActsPage() {
  const { can } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'ks2' | 'ks3'>('ks2');
  const [ks2Open, setKs2Open] = useState(false);
  const [ks3Open, setKs3Open] = useState(false);

  return (
    <AppLayout>
      <PageHeader title="Акты выполненных работ"
        subtitle="КС-2 (приёмка работ) и КС-3 (стоимость работ)"
        actions={<div style={{ display: 'flex', gap: 8 }}>
          {tab === 'ks2' && <Button onClick={() => exportApi.ks2()}><FileDown size={16} /> Excel</Button>}
          {can('acts', 'add') && (tab === 'ks2'
            ? <Button variant="primary" onClick={() => setKs2Open(true)}><Plus size={16} /> Новый КС-2</Button>
            : <Button variant="primary" onClick={() => setKs3Open(true)}><Plus size={16} /> Новая КС-3</Button>)}
        </div>} />

      {/* Вкладки */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
        {(['ks2', 'ks3'] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            style={{
              padding: '8px 18px', borderRadius: 'var(--radius-sm)', fontSize: 14, cursor: 'pointer',
              border: '1px solid var(--border-bright)',
              background: tab === t ? 'var(--bg-card)' : 'transparent',
              color: tab === t ? 'var(--accent)' : 'var(--text-secondary)',
              fontWeight: tab === t ? 500 : 400,
            }}>
            {t === 'ks2' ? 'Акты КС-2' : 'Справки КС-3'}
          </button>
        ))}
      </div>

      {tab === 'ks2'
        ? <KS2List onSign={() => qc.invalidateQueries({ queryKey: ['ks2'] })} />
        : <KS3List />}

      <CreateKS2Modal open={ks2Open} onClose={() => setKs2Open(false)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['ks2'] }); toast('КС-2 создан', 'success'); }}
        onError={(m) => toast(m, 'error')} />
      <CreateKS3Modal open={ks3Open} onClose={() => setKs3Open(false)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['ks3'] }); toast('КС-3 сформирована', 'success'); }}
        onError={(m) => toast(m, 'error')} />
    </AppLayout>
  );
}

function KS2List({ onSign }: { onSign: () => void }) {
  const { can } = useAuth();
  const { toast } = useToast();
  const { data, isLoading } = useQuery({ queryKey: ['ks2'], queryFn: () => ks2Api.list() });

  const pdfM = useMutation({ mutationFn: (id: number) => ks2Api.pdf(id), onSuccess: openBlob,
    onError: (e) => toast(getErrorMessage(e), 'error') });
  const exportM = useMutation({ mutationFn: (id: number) => ks2Api.export1c(id),
    onSuccess: (d) => { toast(`Выгружено в 1С (№ ${d.onec_doc_number})`, 'success'); onSign(); },
    onError: (e) => toast(getErrorMessage(e), 'error') });
  const signM = useMutation({ mutationFn: (id: number) => ks2Api.sign(id),
    onSuccess: () => { toast('КС-2 подписан', 'success'); onSign(); },
    onError: (e) => toast(getErrorMessage(e), 'error') });

  if (isLoading) return <Spinner label="Загрузка КС-2…" />;
  if (!data || data.results.length === 0)
    return <EmptyState icon={<FileCheck size={32} />} title="Актов КС-2 нет" hint="Создайте первый акт приёмки работ" />;

  return (
    <div className="panel">
      <table className="data-table">
        <thead>
          <tr><th>Номер</th><th>Договор</th><th>Период</th><th style={{ textAlign: 'right' }}>Сумма</th><th>1С</th><th>Статус</th><th></th></tr>
        </thead>
        <tbody>
          {data.results.map((a: KS2Act) => (
            <tr key={a.id}>
              <td className="mono">{a.number}</td>
              <td>{a.contract_number}</td>
              <td className="mono">{formatDate(a.period_start)} — {formatDate(a.period_end)}</td>
              <td style={{ textAlign: 'right' }} className="mono">{formatMoney(a.amount)}</td>
              <td>{a.exported_to_1c ? <Check size={15} style={{ color: 'var(--green)' }} /> : '—'}</td>
              <td><StatusBadge label={a.status_display} color={KS2_COLOR[a.status]} /></td>
              <td>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <IconBtn title="Скачать PDF" onClick={() => pdfM.mutate(a.id)} icon={<Download size={15} />} />
                  {can('acts', 'change') && a.status !== 'signed' && (
                    <IconBtn title="Подписать" onClick={() => signM.mutate(a.id)} icon={<PenLine size={15} />} />
                  )}
                  {can('acts', 'change') && !a.exported_to_1c && (
                    <IconBtn title="Выгрузить в 1С" onClick={() => exportM.mutate(a.id)} icon={<FileSpreadsheet size={15} />} />
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KS3List() {
  const { can } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['ks3'], queryFn: () => ks3Api.list() });

  const pdfM = useMutation({ mutationFn: (id: number) => ks3Api.pdf(id), onSuccess: openBlob,
    onError: (e) => toast(getErrorMessage(e), 'error') });
  const paidM = useMutation({ mutationFn: (id: number) => ks3Api.markPaid(id),
    onSuccess: () => { toast('КС-3 отмечена оплаченной', 'success'); qc.invalidateQueries({ queryKey: ['ks3'] }); },
    onError: (e) => toast(getErrorMessage(e), 'error') });

  if (isLoading) return <Spinner label="Загрузка КС-3…" />;
  if (!data || data.results.length === 0)
    return <EmptyState icon={<FileCheck size={32} />} title="Справок КС-3 нет" hint="Сформируйте КС-3 на основе актов КС-2" />;

  return (
    <div className="panel">
      <table className="data-table">
        <thead>
          <tr><th>Номер</th><th>Договор</th><th>Период</th><th style={{ textAlign: 'right' }}>Сумма</th><th style={{ textAlign: 'right' }}>Удержание</th><th>Статус</th><th></th></tr>
        </thead>
        <tbody>
          {data.results.map((c: KS3Certificate) => (
            <tr key={c.id}>
              <td className="mono">{c.number}</td>
              <td>{c.contract_number}</td>
              <td className="mono">{formatDate(c.period_start)} — {formatDate(c.period_end)}</td>
              <td style={{ textAlign: 'right' }} className="mono">{formatMoney(c.amount)}</td>
              <td style={{ textAlign: 'right', color: 'var(--yellow)' }} className="mono">−{formatMoney(c.retention_amount)}</td>
              <td><StatusBadge label={c.status_display} color={KS3_COLOR[c.status]} /></td>
              <td>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <IconBtn title="Скачать PDF" onClick={() => pdfM.mutate(c.id)} icon={<Download size={15} />} />
                  {can('acts', 'change') && c.status !== 'paid' && (
                    <IconBtn title="Отметить оплаченной" onClick={() => paidM.mutate(c.id)} icon={<Banknote size={15} />} />
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IconBtn({ title, onClick, icon }: { title: string; onClick: () => void; icon: React.ReactNode }) {
  return (
    <button title={title} onClick={onClick}
      style={{ background: 'transparent', border: '1px solid var(--border-bright)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
      {icon}
    </button>
  );
}

function CreateKS2Modal({ open, onClose, onCreated, onError }: {
  open: boolean; onClose: () => void; onCreated: () => void; onError: (m: string) => void;
}) {
  const [number, setNumber] = useState('');
  const [contract, setContract] = useState<number | ''>('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [amount, setAmount] = useState('');
  const { data: contracts } = useQuery({ queryKey: ['contracts-all'], queryFn: () => contractsApi.list({}), enabled: open });

  const m = useMutation({
    mutationFn: () => ks2Api.create({
      number, contract: Number(contract), period_start: start, period_end: end, amount,
    }),
    onSuccess: () => { onClose(); reset(); onCreated(); },
    onError: (e) => onError(getErrorMessage(e)),
  });
  const reset = () => { setNumber(''); setContract(''); setStart(''); setEnd(''); setAmount(''); };

  return (
    <Modal open={open} onClose={onClose} title="Новый акт КС-2"
      footer={<>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="primary" disabled={!number || !contract || !start || !end || m.isPending} onClick={() => m.mutate()}>
          {m.isPending ? 'Создание…' : 'Создать'}
        </Button>
      </>}>
      <div className="field"><label className="field__label">Номер КС-2</label>
        <input className="field__input" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="КС2-2026-001" /></div>
      <div className="field"><label className="field__label">Договор</label>
        <select className="field__select" value={contract} onChange={(e) => setContract(e.target.value ? Number(e.target.value) : '')}>
          <option value="">— выберите —</option>
          {contracts?.results.map((c) => <option key={c.id} value={c.id}>{c.number}</option>)}
        </select></div>
      <div style={{ display: 'flex', gap: 12 }}>
        <div className="field" style={{ flex: 1 }}><label className="field__label">Период с</label>
          <input type="date" className="field__input" value={start} onChange={(e) => setStart(e.target.value)} /></div>
        <div className="field" style={{ flex: 1 }}><label className="field__label">по</label>
          <input type="date" className="field__input" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
      </div>
      <div className="field"><label className="field__label">Сумma к приёмке, ₽</label>
        <input className="field__input" value={amount} inputMode="decimal" onChange={(e) => setAmount(e.target.value)} placeholder="1500000" /></div>
    </Modal>
  );
}

function CreateKS3Modal({ open, onClose, onCreated, onError }: {
  open: boolean; onClose: () => void; onCreated: () => void; onError: (m: string) => void;
}) {
  const [number, setNumber] = useState('');
  const [contract, setContract] = useState<number | ''>('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [selectedKs2, setSelectedKs2] = useState<number[]>([]);

  const { data: contracts } = useQuery({ queryKey: ['contracts-all'], queryFn: () => contractsApi.list({}), enabled: open });
  // КС-2 выбранного договора (только подписанные/готовые)
  const { data: ks2 } = useQuery({
    queryKey: ['ks2-for-contract', contract],
    queryFn: () => ks2Api.list({ contract: Number(contract) }),
    enabled: open && !!contract,
  });

  const toggleKs2 = (id: number) =>
    setSelectedKs2((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const m = useMutation({
    mutationFn: () => ks3Api.createFromKs2(Number(contract), selectedKs2, start, end, number),
    onSuccess: () => { onClose(); reset(); onCreated(); },
    onError: (e) => onError(getErrorMessage(e)),
  });
  const reset = () => { setNumber(''); setContract(''); setStart(''); setEnd(''); setSelectedKs2([]); };

  return (
    <Modal open={open} onClose={onClose} title="Сформировать КС-3 из актов КС-2" width={560}
      footer={<>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="primary" disabled={!number || !contract || selectedKs2.length === 0 || !start || !end || m.isPending} onClick={() => m.mutate()}>
          {m.isPending ? 'Формирование…' : 'Сформировать'}
        </Button>
      </>}>
      <div className="field"><label className="field__label">Номер КС-3</label>
        <input className="field__input" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="КС3-2026-001" /></div>
      <div className="field"><label className="field__label">Договор</label>
        <select className="field__select" value={contract} onChange={(e) => { setContract(e.target.value ? Number(e.target.value) : ''); setSelectedKs2([]); }}>
          <option value="">— выберите —</option>
          {contracts?.results.map((c) => <option key={c.id} value={c.id}>{c.number}</option>)}
        </select></div>
      {contract && (
        <div className="field">
          <label className="field__label">Акты КС-2 для включения</label>
          {!ks2 || ks2.results.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>По этому договору нет актов КС-2</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
              {ks2.results.map((a) => (
                <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedKs2.includes(a.id)} onChange={() => toggleKs2(a.id)} />
                  <span className="mono" style={{ fontSize: 13 }}>{a.number}</span>
                  <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 13 }}>{formatMoney(a.amount)}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 12 }}>
        <div className="field" style={{ flex: 1 }}><label className="field__label">Период с</label>
          <input type="date" className="field__input" value={start} onChange={(e) => setStart(e.target.value)} /></div>
        <div className="field" style={{ flex: 1 }}><label className="field__label">по</label>
          <input type="date" className="field__input" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
      </div>
    </Modal>
  );
}
